import { nextTick, onScopeDispose, watch } from 'vue';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/** Обработчики активных ловушек в порядке активации: Tab ловит только последняя. */
const activeTraps = new Set<(event: KeyboardEvent) => void>();

/**
 * Минимальный focus-trap без внешних зависимостей.
 *
 * При активации: запоминает текущий активный элемент (триггер), фокусирует
 * элемент `[data-autofocus]`, иначе первый фокусируемый внутри контейнера (или
 * сам контейнер), зацикливает Tab/Shift+Tab внутри контейнера. При деактивации:
 * возвращает фокус на триггер. Ловушки вкладываются: диалог поверх редактора
 * держит Tab у себя, пока открыт.
 *
 * Контейнер должен иметь `tabindex="-1"` — фолбэк фокуса, если внутри нет
 * фокусируемых элементов. Вынесен в shared/lib для переиспользования модалками и панелями.
 *
 * @param getContainer геттер DOM-узла ловушки (может вернуть null, пока закрыт)
 * @param isActive геттер флага активности (обычно `() => props.open`)
 */
export function useFocusTrap(getContainer: () => HTMLElement | null, isActive: () => boolean): void {
  let previouslyFocused: HTMLElement | null = null;

  const focusable = (container: HTMLElement): HTMLElement[] =>
    Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    );

  const handleKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Tab' || [...activeTraps].at(-1) !== handleKeydown) {
      return;
    }

    const container = getContainer();
    if (!container) {
      return;
    }

    const items = focusable(container);
    const first = items[0];
    const last = items[items.length - 1];

    // Нет фокусируемых — держим фокус на самом контейнере.
    if (!first || !last) {
      event.preventDefault();
      container.focus();
      return;
    }

    const active = document.activeElement as HTMLElement | null;

    if (event.shiftKey) {
      if (active === first || active === container || !container.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last || !container.contains(active)) {
      event.preventDefault();
      first.focus();
    }
  };

  const activate = async (): Promise<void> => {
    if (typeof document === 'undefined') {
      return;
    }

    previouslyFocused = document.activeElement as HTMLElement | null;
    activeTraps.add(handleKeydown);
    document.addEventListener('keydown', handleKeydown, true);

    // Ждём монтирования контейнера (v-if + Transition), затем ставим фокус внутрь.
    await nextTick();

    const container = getContainer();
    if (!container) {
      return;
    }

    (container.querySelector<HTMLElement>('[data-autofocus]') ?? focusable(container)[0] ?? container).focus();
  };

  const deactivate = (): void => {
    if (typeof document === 'undefined') {
      return;
    }

    activeTraps.delete(handleKeydown);
    document.removeEventListener('keydown', handleKeydown, true);
    previouslyFocused?.focus?.();
    previouslyFocused = null;
  };

  // `immediate`: контейнер может быть открыт с самого начала — прямая ссылка на
  // панель монтирует её сразу, и без первого срабатывания ловушки бы не было.
  watch(
    isActive,
    (active) => {
      if (active) {
        void activate();
      } else {
        deactivate();
      }
    },
    { immediate: true },
  );

  // Размонтирование открытого контейнера — то же закрытие: фокус возвращается на триггер.
  onScopeDispose(() => {
    deactivate();
  });
}
